import roles from "roles";

/**
 * Permissiemodel van het CMS.
 *
 * Eén permissie = één ding dat je in het CMS kunt zien of doen. Menu-items,
 * routes, knoppen en het dataladen vragen `can(permissie)`; ze kijken nooit
 * rechtstreeks naar de rolnaam.
 *
 * Bron van "wat mag een rol" (BMX-36):
 * 1. Firestore `cmsRoles/{roleId}.permissions` — instelbaar via Beheer → Rollen.
 * 2. Ontbreekt dat document (of is het ongeldig): DEFAULT_ROLE_PERMISSIONS hier.
 * 3. Per gebruiker: `cmsPermissions: { grant: [], deny: [] }` op het profiel.
 * Effectief = rolrechten + grant − deny. Beheerder heeft altijd alles.
 *
 * Landfiltering / klantselectie staat hier los van: dat is een *scope* op data
 * (klanten en orders), geen permissie. Zie getScope.
 *
 * Houd deze module vrij van React en Firestore: hij wordt ook gebruikt om de
 * Firestore-regels (docs/firestore-rules-bmx36.rules) mee te spiegelen.
 */
export const PERMISSIONS = {
  REPORTS_VIEW: "reports.view",
  PRODUCTS_MANAGE: "products.manage",
  CATEGORIES_MANAGE: "categories.manage",
  DISCOUNTS_MANAGE: "discounts.manage",
  ORDERS_VIEW: "orders.view",
  ORDERS_MANAGE: "orders.manage",
  ORDERS_EXACT: "orders.exact",
  QUOTES_VIEW: "quotes.view",
  QUOTES_MANAGE: "quotes.manage",
  SHIPMENTS_MANAGE: "shipments.manage",
  CUSTOMERS_VIEW: "customers.view",
  CUSTOMERS_MANAGE: "customers.manage",
  CUSTOMERS_PENDING: "customers.pending",
  CUSTOMERS_AUDIENCES: "customers.audiences",
  CUSTOMERS_MAP: "customers.map",
  CRM_WRITE: "crm.write",
  MARKETING_SEND: "marketing.send",
  DEALERS_MANAGE: "dealers.manage",
  PURCHASING_MANAGE: "purchasing.manage",
  ADMIN_USERS: "admin.users",
  ADMIN_GROUPS: "admin.groups",
  ADMIN_COLLECTIONS: "admin.collections",
  ADMIN_DOCUMENTATION: "admin.documentation",
};

export const ALL_PERMISSIONS = Object.values(PERMISSIONS);

/**
 * Indeling voor de UI (Beheer → Rollen, rechten per gebruiker). Volgorde is
 * de volgorde op het scherm. Labels/uitleg staan in nl.json onder
 * `permissions.<key>` en `permission_groups.<group>`.
 */
export const PERMISSION_GROUPS = [
  {
    key: "sales",
    permissions: [
      PERMISSIONS.REPORTS_VIEW,
      PERMISSIONS.PRODUCTS_MANAGE,
      PERMISSIONS.CATEGORIES_MANAGE,
      PERMISSIONS.DISCOUNTS_MANAGE,
      PERMISSIONS.ORDERS_VIEW,
      PERMISSIONS.ORDERS_MANAGE,
      PERMISSIONS.ORDERS_EXACT,
      PERMISSIONS.QUOTES_VIEW,
      PERMISSIONS.QUOTES_MANAGE,
      PERMISSIONS.SHIPMENTS_MANAGE,
      PERMISSIONS.DEALERS_MANAGE,
    ],
  },
  {
    key: "customers",
    permissions: [
      PERMISSIONS.CUSTOMERS_VIEW,
      PERMISSIONS.CUSTOMERS_MAP,
      PERMISSIONS.CUSTOMERS_MANAGE,
      PERMISSIONS.CUSTOMERS_PENDING,
      PERMISSIONS.CUSTOMERS_AUDIENCES,
      PERMISSIONS.CRM_WRITE,
      PERMISSIONS.MARKETING_SEND,
    ],
  },
  {
    key: "purchasing",
    permissions: [PERMISSIONS.PURCHASING_MANAGE],
  },
  {
    key: "admin",
    permissions: [
      PERMISSIONS.ADMIN_USERS,
      PERMISSIONS.ADMIN_GROUPS,
      PERMISSIONS.ADMIN_COLLECTIONS,
      PERMISSIONS.ADMIN_DOCUMENTATION,
    ],
  },
];

const ADMIN_PERMISSIONS = [
  PERMISSIONS.ADMIN_USERS,
  PERMISSIONS.ADMIN_GROUPS,
  PERMISSIONS.ADMIN_COLLECTIONS,
  PERMISSIONS.ADMIN_DOCUMENTATION,
];

/**
 * Standaardrechten per rol. Dit is het vangnet én de "terug naar standaard"
 * in Beheer → Rollen. De werkelijke rechten staan in Firestore.
 */
export const DEFAULT_ROLE_PERMISSIONS = {
  // Beheerder: alles, altijd. Niet instelbaar.
  [roles.admin]: ALL_PERMISSIONS,
  // Gebruiker: alles behalve beheer (historisch gedrag).
  [roles.user]: ALL_PERMISSIONS.filter((p) => !ADMIN_PERMISSIONS.includes(p)),
  // Verkoper: eigen klanten bekijken, CRM bijhouden, hun orders inzien, offertes maken (BMX-58).
  [roles.sales]: [
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_MAP,
    PERMISSIONS.CRM_WRITE,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.QUOTES_VIEW,
    PERMISSIONS.QUOTES_MANAGE,
  ],
  // Interne medewerker (klantcontact, orderafhandeling): klanten en orders
  // volledig, CRM, marketingmail, verzendingen. Geen producten/inkoop/
  // rapportages/beheer.
  [roles.employee]: [
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_MAP,
    PERMISSIONS.CUSTOMERS_MANAGE,
    PERMISSIONS.CUSTOMERS_PENDING,
    PERMISSIONS.CUSTOMERS_AUDIENCES,
    PERMISSIONS.CRM_WRITE,
    PERMISSIONS.MARKETING_SEND,
    PERMISSIONS.ORDERS_VIEW,
    PERMISSIONS.ORDERS_MANAGE,
    PERMISSIONS.SHIPMENTS_MANAGE,
  ],
  // Inkoop / productbeheer: producten, categorieën, inkoop; orders alleen inzien.
  [roles.purchasing]: [
    PERMISSIONS.PRODUCTS_MANAGE,
    PERMISSIONS.CATEGORIES_MANAGE,
    PERMISSIONS.PURCHASING_MANAGE,
    PERMISSIONS.ORDERS_VIEW,
  ],
};

/** @deprecated gebruik DEFAULT_ROLE_PERMISSIONS / getEffectivePermissions. */
export const ROLE_PERMISSIONS = DEFAULT_ROLE_PERMISSIONS;

/** Volgorde in selects en op Beheer → Rollen. */
export const ROLE_ORDER = [roles.admin, roles.user, roles.employee, roles.purchasing, roles.sales];

export const CMS_ROLES = Object.values(roles);

export const isCmsRole = (role) => CMS_ROLES.includes(role);

/**
 * De CMS-rol van een profiel (users/{uid}).
 *
 * Twee bronnen, in deze volgorde:
 * 1. `cmsRole` — voor een bestaand webshopaccount (role blijft "webshopUser",
 *    zodat webshop-login, klantenlijst en verkopersrapport niet breken) dat
 *    daarnaast CMS-toegang heeft gekregen.
 * 2. `role` — voor accounts die via een CMS-uitnodiging zijn aangemaakt.
 *
 * null = geen CMS-toegang.
 */
export const getCmsRole = (profile) => {
  if (!profile) return null;
  if (isCmsRole(profile.cmsRole)) return profile.cmsRole;
  if (isCmsRole(profile.role)) return profile.role;
  return null;
};

/** Waar de CMS-rol van dit profiel is opgeslagen: "role" of "cmsRole". */
export const getCmsRoleField = (profile) => {
  if (!profile) return "role";
  if (isCmsRole(profile.role)) return "role";
  return "cmsRole";
};

/** Een webshopaccount dat óók CMS-toegang heeft (rol staat in `cmsRole`). */
export const isLinkedWebshopAccount = (profile) =>
  !!profile && !isCmsRole(profile.role) && isCmsRole(profile.cmsRole);

const knownPermissions = (list) =>
  Array.isArray(list) ? list.filter((p) => typeof p === "string" && ALL_PERMISSIONS.includes(p)) : [];

/**
 * Rechten van een rol: uit het Firestore-document (`roleDocs[roleId]`,
 * bijv. via useSelector op `cmsRoles`) als dat een geldige `permissions`-array
 * heeft, anders de standaard. Beheerder altijd alles.
 */
export const getRolePermissions = (role, roleDocs) => {
  if (!isCmsRole(role)) return [];
  if (role === roles.admin) return ALL_PERMISSIONS;
  const doc = roleDocs ? roleDocs[role] : null;
  if (doc && Array.isArray(doc.permissions)) return knownPermissions(doc.permissions);
  return DEFAULT_ROLE_PERMISSIONS[role] || [];
};

/** @deprecated standaardrechten; gebruik getRolePermissions met roleDocs. */
export const getPermissionsForRole = (role) => getRolePermissions(role, null);

/**
 * Effectieve rechten van een profiel: rolrechten + `cmsPermissions.grant`
 * − `cmsPermissions.deny`. Beheerder: alles, overrides genegeerd.
 * Retourneert een array in de volgorde van ALL_PERMISSIONS.
 */
export const getEffectivePermissions = (profile, roleDocs) => {
  const role = getCmsRole(profile);
  if (!role) return [];
  if (role === roles.admin) return ALL_PERMISSIONS;
  const base = new Set(getRolePermissions(role, roleDocs));
  const overrides = profile.cmsPermissions || {};
  knownPermissions(overrides.grant).forEach((p) => base.add(p));
  knownPermissions(overrides.deny).forEach((p) => base.delete(p));
  return ALL_PERMISSIONS.filter((p) => base.has(p));
};

/** Standaardrechten van een rol bevatten deze permissie (zonder Firestore/overrides). */
export const hasPermission = (role, permission) => getRolePermissions(role, null).includes(permission);

const normalizeCountry = (code) => (typeof code === "string" ? code.trim().toUpperCase() : null);

export const normalizeCountries = (countries) =>
  Array.isArray(countries)
    ? countries.map(normalizeCountry).filter((c, i, arr) => !!c && arr.indexOf(c) === i)
    : [];

const normalizeIds = (ids) =>
  Array.isArray(ids) ? ids.filter((id, i, arr) => typeof id === "string" && !!id && arr.indexOf(id) === i) : [];

export const SCOPE_ALL = "all";
export const SCOPE_COUNTRIES = "countries";
export const SCOPE_CUSTOMERS = "customers";
export const SCOPE_NONE = "none";

/**
 * Welke klanten (en dus orders) dit profiel ziet.
 *
 * - `{ type: "all" }` — geen beperking: beheerder altijd; andere rollen zonder
 *   landen én zonder klantselectie (bestaande cmsUsers zien alles, zoals nu).
 * - `{ type: "customers", customerIds }` — alleen deze klanten
 *   (`salesCustomerIds` op het profiel). Gaat vóór landen.
 * - `{ type: "countries", countries }` — klanten uit deze landen
 *   (`salesCountries`, ISO-2; dezelfde veldnaam als de webshop voor verkopers).
 * - `{ type: "none" }` — verkoper zonder landen en zonder klanten ziet niets.
 *   Veilige standaard.
 */
export const getScope = (profile) => {
  const role = getCmsRole(profile);
  if (!role || role === roles.admin) return { type: SCOPE_ALL };
  const customerIds = normalizeIds(profile.salesCustomerIds);
  if (customerIds.length > 0) return { type: SCOPE_CUSTOMERS, customerIds };
  const countries = normalizeCountries(profile.salesCountries);
  if (countries.length > 0) return { type: SCOPE_COUNTRIES, countries };
  return role === roles.sales ? { type: SCOPE_NONE } : { type: SCOPE_ALL };
};

/**
 * Landen waartoe dit profiel beperkt is: lijst bij landen-scope, `[]` bij
 * "niets", anders null (geen landbeperking — let op: bij een klantselectie is
 * dit óók null; gebruik getScope waar dat verschil telt).
 */
export const getAllowedCountries = (profile) => {
  const scope = getScope(profile);
  if (scope.type === SCOPE_COUNTRIES) return scope.countries;
  if (scope.type === SCOPE_NONE) return [];
  return null;
};

/** null = alles toegestaan. */
export const isCountryAllowed = (allowedCountries, countryCode) => {
  if (allowedCountries === null || allowedCountries === undefined) return true;
  const code = normalizeCountry(countryCode);
  return !!code && allowedCountries.includes(code);
};

const asScope = (scopeOrCountries) => {
  // Backwards compatibel: een landenlijst of null mag ook.
  if (scopeOrCountries === null || scopeOrCountries === undefined) return { type: SCOPE_ALL };
  if (Array.isArray(scopeOrCountries)) {
    return scopeOrCountries.length > 0
      ? { type: SCOPE_COUNTRIES, countries: normalizeCountries(scopeOrCountries) }
      : { type: SCOPE_NONE };
  }
  return scopeOrCountries;
};

/** Klanten (users) beperken tot de scope. Land staat op `company.country`. */
export const scopeCustomers = (customers, scopeOrCountries) => {
  if (!Array.isArray(customers)) return customers;
  const scope = asScope(scopeOrCountries);
  switch (scope.type) {
    case SCOPE_ALL:
      return customers;
    case SCOPE_COUNTRIES:
      return customers.filter((customer) => isCountryAllowed(scope.countries, customer?.company?.country));
    case SCOPE_CUSTOMERS: {
      const ids = new Set(scope.customerIds);
      return customers.filter((customer) => ids.has(customer?.id));
    }
    default:
      return [];
  }
};

/**
 * Orders beperken tot de scope. De webshop zet bij het afrekenen
 * `customer.country` (ISO-2) en `customer.uid` op de order.
 * - landen: land past, óf de klant zit in de al gefilterde klantenlijst
 *   (vangnet voor oudere orders zonder landcode);
 * - klanten: `customer.uid` in de selectie.
 */
export const scopeOrders = (orders, scopeOrCountries, scopedCustomers) => {
  if (!Array.isArray(orders)) return orders;
  const scope = asScope(scopeOrCountries);
  if (scope.type === SCOPE_ALL) return orders;
  if (scope.type === SCOPE_NONE) return [];
  const customerIds = new Set(
    scope.type === SCOPE_CUSTOMERS ? scope.customerIds : (scopedCustomers || []).map((customer) => customer.id),
  );
  return orders.filter((order) => {
    const customer = order?.customer;
    if (!customer) return false;
    if (scope.type === SCOPE_COUNTRIES && isCountryAllowed(scope.countries, customer.country)) return true;
    return !!customer.uid && customerIds.has(customer.uid);
  });
};
